import { types, Instance } from "mobx-state-tree";
import { StorageModel } from "../storage/StorageModel";
import { endpoints } from "../api/ApiEndpoints";
import { api } from "../api/ApiService";
import { dateService } from "../date-service/DateService";
import { authentication } from "../authentication/Authentication";
import { organizationSubscriptionService } from "../organization/OrganizationSubscription";

// When ONE file's results — and with them every link that points at them — are removed.
// Both dates come from the server: expiration is the file's own Created plus the FileExpiration
// retention window, the same rule the backend's FileExpirationWorker deletes by.
export interface FileExpirationResponse {
    fileId: number;
    created: string;
    expiration: string;
}

const FileExpirationModel = types.model({
    fileId: types.optional(types.number, 0),
    file: types.optional(types.frozen<FileExpirationResponse | undefined>(), undefined),
});

// A per-file expiration lookup, because no list can answer this reliably: the shared history
// store is filtered to running files and to a single page of rows, so the finished file someone
// is about to share a link to is normally missing from it.
export const FileExpirationStorageModel = types
    .compose(FileExpirationModel, StorageModel)
    .named("FileExpirationStorageModel")
    .actions((self) => ({
        mapResponse(response?: FileExpirationResponse) {
            self.file = response ?? undefined;
        },
        setFileId(fileId: number) {
            self.fileId = fileId;
        },
    }))
    .actions((self) => ({
        // Point the store at a file and fetch its dates. Drops the previous file's answer first
        // so nothing reads the old date while the new request is in flight.
        loadForFile(fileId: number) {
            if (self.fileId === fileId && self.file?.fileId === fileId) return Promise.resolve();
            self.setFileId(fileId);
            self.mapResponse(undefined);
            return self.load();
        },
        clear() {
            self.setFileId(0);
            self.mapResponse(undefined);
        },
    }))
    .views((self) => ({
        // Same rule the history store uses: an anonymous session can only reach the shop
        // endpoints with an organization-subscription key, and firing without one buys a
        // guaranteed 401 (which surfaces to the viewer as an error, not a missing date).
        get canLoadItems() {
            if (self.fileId <= 0) return false;
            if (!authentication.anonymous) return true;
            return !!organizationSubscriptionService?.key;
        },
        get endpoint() {
            const endpoint = endpoints.get("shop_file_expiration");
            endpoint.routeParams = [self.fileId];
            return endpoint;
        },
        // Answers only for the file actually loaded, so a caller on a different file gets
        // undefined rather than the previous file's date.
        getExpirationForFile(fileId?: number): Date | undefined {
            if (fileId === undefined || self.file?.fileId !== fileId) return undefined;
            return dateService.parseApiDateTime(self.file.expiration);
        },
        getCreatedForFile(fileId?: number): Date | undefined {
            if (fileId === undefined || self.file?.fileId !== fileId) return undefined;
            return dateService.parseApiDateTime(self.file.created);
        },
    }));

export type FileExpirationStorage = Instance<typeof FileExpirationStorageModel>;

export const fileExpirationStorage = FileExpirationStorageModel.create(
    { autoLoad: false },
    { api }
);
